import { FocusMonitor } from '@angular/cdk/a11y'
import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Optional, Self, SimpleChanges } from '@angular/core'
import { FormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms'
import { MatFormFieldControl } from '@angular/material/form-field'
import { Resource, ResourcesService, ResourceType } from '@mmx/shared'
import { Dictionary, filter, groupBy, intersection, isArray, isEmpty, map as _map, orderBy } from 'lodash'
import { Observable, ReplaySubject, Subject } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'

import { CustomMaterialInputControlComponent } from '../custom-field'

@Component({
  selector: 'mmx-resource-selector-input',
  templateUrl: 'resource-selector.component.html',
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: ResourceSelectorFieldComponent,
    },
  ],
  standalone: false,
})
export class ResourceSelectorFieldComponent
  extends CustomMaterialInputControlComponent<string[]>
  implements OnInit, OnChanges, OnDestroy
{
  resourceFormControl = new FormControl<string[]>(null)
  allResources: Resource[] = []

  /**
   * Facility-specific and global resources
  */
  resources: Resource[] = []

  filteredResources$ = new ReplaySubject<Resource[]>(1)
  filteredGroupResources$: Observable<Dictionary<Resource[]>> = this.filteredResources$.pipe(
    map(resources => groupBy(orderBy(resources, ['type', 'name']), 'type')),
  )
  resourceFilterControl = new FormControl<string>(null)

  @Input() type: ResourceType | ResourceType[]
  @Input() groupByType = true
  /**
   * Filter for resources explicitly assigned to this facility
   */
  @Input() facilityId: string
  @Input() multiple = true

  @Input()
  get value(): string[] {
    return this._value || []
  }
  set value(resources: string[]) {
    this._value = resources || []
    this.stateChanges.next()
    if (this.onChange) {
      this.onChange(this.value)
    }
  }

  protected _placeholder = 'Resources'
  declare _value: string[]

  private onDestroy$ = new Subject<void>()

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() protected parentForm: NgForm,
    @Optional() protected parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
    private resourcesService: ResourcesService,
  ) {
    super(ngControl, parentForm, parentFormGroup, fm, elRef)
  }

  ngOnInit() {
    super.ngOnInit()

    this.resourcesService.resources$.pipe(takeUntil(this.onDestroy$)).subscribe((resources) => {
      this.allResources = resources
      this.filterResourcesByFacility()

      if (this.value != null) {
        this.writeValue(this._value)
      }

      const filteredResources = this.getFilteredResources()
      this.filteredResources$.next(filteredResources)

      this.resourceFilterControl.valueChanges
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          this.filterResources()
        })

      this.resourceFormControl.valueChanges.subscribe((value) => {
        this.value = value
      })

      if (this.value) {
        // todo fix this component and control typings.
        if (this.multiple) {
          this.resourceFormControl.setValue(this.value)
        } else {
          this.resourceFormControl.setValue(this.value[0] as any)
        }
      }
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.facilityId && this.resources) {
      this.filterResourcesByFacility()
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  writeValue(value: Resource[] | Resource | string[] | string) {
    if (!isArray(value)) {
      value = [value] as any
    }

    this.value = filter(
      _map(value as (string | Resource)[], (r) => {
        if (typeof r === 'string') {
          const resource = this.resources.find((re) => re.id === r)
          if (resource) {
            r = resource
          } else {
            return r
          }
        }

        if (r instanceof Resource && this.typeMatch(r.type)) {
          return r.id
        }
      }),
    )

    if (this.multiple) {
      this.resourceFormControl.setValue(this.value)
    } else {
      this.resourceFormControl.setValue(this.value[0] as any)
    }
  }

  private filterResourcesByFacility() {
    if (this.allResources) {
      this.resources = this.allResources.filter(resource => {
        return !this.facilityId || isEmpty(resource.facilityIds) || resource.facilityIds.includes(this.facilityId)
      })

      this.filterResources()
    }
  }

  private filterResources() {
    if (!this.resources) {
      return
    }
    // get the search keyword
    let search = this.resourceFilterControl.value
    if (!search) {
      this.filteredResources$.next(this.getFilteredResources())
      return
    } else {
      search = search.toLowerCase()
    }
    // filter the banks
    this.filteredResources$.next(
      this.getFilteredResources().filter(
        (resource) => resource.name.toLowerCase().indexOf(search) > -1,
      ),
    )
  }

  private getFilteredResources() {
    return this.resources.filter(resource => this.typeMatch(resource.type ?? 'other'))
  }

  private typeMatch(type: string = 'other') {
    return (
      this.type == null ||
      (typeof this.type === 'string' && type === this.type) ||
      (isArray(this.type) && intersection(this.type, [type]).length > 0)
    )
  }
}
